/**
 * useImportAnonymousShortlist — auto-imports listing IDs that were saved
 * via an embed widget on an agent's website (stored under per-agent
 * `lvh.shortlist.<slug>` keys in localStorage) into the user's lovinahome
 * account once they sign up or log in.
 *
 * Wire-in: import + call this hook once inside a top-level authed component
 * (e.g. App.js or any layout that mounts when token is present). It runs
 * exactly once per user session.
 */
import { useEffect } from 'react';
import axios from 'axios';
import { toast } from './use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FLAG_KEY = 'lvh.shortlistImported.v1';

const collectListings = () => {
  const all = new Set();
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('lvh.shortlist.')) continue;
    try {
      const arr = JSON.parse(localStorage.getItem(k) || '[]');
      if (Array.isArray(arr)) arr.forEach((x) => x && all.add(String(x)));
    } catch { /* ignore corrupted entries */ }
  }
  return Array.from(all);
};

export const useImportAnonymousShortlist = (token) => {
  useEffect(() => {
    if (!token) return;
    if (localStorage.getItem(FLAG_KEY) === '1') return;
    const ids = collectListings();
    if (ids.length === 0) {
      // No anonymous shortlist — mark the flag so we don't keep scanning.
      localStorage.setItem(FLAG_KEY, '1');
      return;
    }
    (async () => {
      try {
        const r = await axios.post(
          `${BACKEND_URL}/api/embed/shortlist/import`,
          { listing_ids: ids },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const imported = r.data?.imported || 0;
        if (imported > 0) {
          toast({
            title: `Welcome — imported ${imported} saved listing${imported === 1 ? '' : 's'}`,
            description: 'They were on your shortlist from an agent\u2019s website. View them in My Saved.',
          });
        }
        // Clear the localStorage keys we just imported so they don't fire again.
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('lvh.shortlist.')) localStorage.removeItem(k);
        }
        localStorage.setItem(FLAG_KEY, '1');
      } catch {
        // Silent — we'll retry on next session if it failed.
      }
    })();
  }, [token]);
};

export default useImportAnonymousShortlist;
